import React, { useEffect, useState } from 'react';
import { useAuth, useAuthActions } from '../../context/AuthContext';

// BAR-135: persistent banner shown at the top of the shell while a super_admin
// is impersonating a user. Displays the target identity, a live mm:ss countdown
// to the 60-min session ceiling, and an Exit action that refreshes + restores
// the real admin (App.exitImpersonation, §6.4). Rendered by ShellLayout above
// the route <Outlet/> so it is visible on every authed view during a session.

function formatRemaining(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function ImpersonationBanner() {
  const { impersonation } = useAuth();
  const { exitImpersonation } = useAuthActions();
  const [now, setNow] = useState(() => Date.now());
  const [exiting, setExiting] = useState(false);

  // 1s tick drives the countdown while a session is active.
  useEffect(() => {
    if (!impersonation) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [impersonation]);

  if (!impersonation) return null;

  const expiresMs = new Date(impersonation.expires_at).getTime();
  const remaining = formatRemaining(expiresMs - now);

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await exitImpersonation?.();
    } finally {
      // The component unmounts (or impersonation clears) on success; reset for
      // the degrade-to-logout path where it may briefly remain mounted.
      setExiting(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-3 px-4 py-1.5 text-[13px] font-medium bg-warning-bg text-warning border-b border-warning"
    >
      <span className="inline-flex items-center gap-1.5">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
        <span>
          Impersonating <strong className="font-semibold">{impersonation.target_email || 'user'}</strong>
        </span>
      </span>
      <span className="text-fg-secondary" aria-hidden="true">·</span>
      <span aria-label={`${remaining} remaining`}>{remaining} left</span>
      <span className="text-fg-secondary" aria-hidden="true">·</span>
      <button
        type="button"
        onClick={handleExit}
        disabled={exiting}
        className="underline hover:no-underline font-semibold disabled:opacity-60"
      >
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  );
}
